import { useState, useRef, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
//import { toast } from "react-toastify";
//import * as message from "../../../constants/messages";

import {
  Box, Breadcrumbs, Button,
  Card, CardHeader, CardContent, Collapse,
  FormControl,
  IconButton, InputAdornment,
  MenuItem,
  OutlinedInput,
  Select,
  Typography, TextField, Tooltip,
  useTheme 
} from "@mui/material";
import Grid from "@mui/material/Grid";

import styled from "styled-components";

import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

//import ReactECharts from 'echarts-for-react';  // or var ReactECharts = require('echarts-for-react');
import * as echarts from 'echarts'; // ECharts 3.0.2 호환

import { 
  useGetTableData, useGetTableDataInQueryString 
} from "../../../components/Table/hooks";

import { monitoringResultMonthlyInfoColumns } from "../../../constants/columns";
import { TableTemplate, NewTableTemplate } from "../../../components/Table/TableTemplate";
import { ModalTemplate } from "../../../components/Table/ModalTemplate";
import {
  BreadcrumbsTable, 
  CollapsibleCardTemplate
} from "../../../components/common/HelperFunc";
import { FONT_PROPS, COLORS } from "../../../styles/colors";

///////////////////////////////
// 스타일
///////////////////////////////

// 테이블 컨테이너 스타일
const TableContainer = styled.div`
  width: 100%;
  height: auto;
  padding: 16px 32px 16px 32px;
  display: flex;
  flex-direction: column;
`;

//////////////////////////////////
// 위험성평가 결과 컴포넌트
//////////////////////////////////
const PARENT_TITLE = "모니터링";
const CHILD_TITLE = "위험성평가 결과";
export function MonitoringResult() {
  const theme = useTheme();
  const { group_code, project_code } = useParams();

  ////////////////////////
  // CRUD 요청
  ////////////////////////

  // 데이터 조회에 필요한 평가연도를 상태로 관리합니다.
  const [searchYearStatus, setSearchYearStatus] = useState({});
  // 데이터 조회에 필요한 설비명을 상태로 관리합니다.
  const [searchMachineStatus, setSearchMachineStatus] = useState({});
  // 데이터 조회에 필요한 쿼리스트링을 만듭니다.
  const qeuryString = { 
    params: { // 쿼리스트링 만들때 params를 사용하는게 규칙입니다.
        year: searchYearStatus.year,
        machine_id: searchMachineStatus.machine_id,
    },
  } 

  // 위험요인 지표 조회 요청
  const getRegisterCauseData = useGetTableDataInQueryString(
    "request",
    "/getMonitoringResultRegisterCause",
    group_code,
    project_code,
    qeuryString,
  );

  // 위험성 평가완료 지표 조회 요청
  const getEvaluationData = useGetTableDataInQueryString(
    "request",
    "/getMonitoringResultEvaluation",
    group_code,
    project_code,
    qeuryString,
  );

  // 설비별 종합 위험성 지표 조회 요청
  const getMachineData = useGetTableDataInQueryString(
    "request",
    "/getMonitoringResultMachine",
    group_code,
    project_code,
    qeuryString,
  );

  // 평가년도 필터링 목록 조회 요청
  const getFilterYearListData = useGetTableData(
    "request",
    "/getMonitoringResultFilterYearList",
    group_code,
    project_code,
  );
  // 설비명 필터링 목록 조회 요청
  const getFilterMachineListData = useGetTableDataInQueryString(
    "request",
    "/getMonitoringResultFilterMachineList",
    group_code,
    project_code,
    qeuryString,
  );
  
  // 평가연도 필터 초기값 설정
  useEffect(() => {
    // 데이터가 로드되지 않았거나 비어 있는 경우 처리 중단
    if(!getFilterYearListData.data || getFilterYearListData.data.length === 0 ) {
      return;
    }
    
    // 최신 평가연도 데이터 가져오기
    let latestYearData = null;
    for(let i = getFilterYearListData.data.length - 1; i >= 0; i--) {
      if(getFilterYearListData.data[i].eval_year) {
        latestYearData = getFilterYearListData.data[i];
        break;
      }
    }
    if(!latestYearData) {
      return; // 최신 평가연도 데이터가 없으면 처리 중단
    }

    const data = {
      year: latestYearData.eval_year,
    };
    
    setSearchYearStatus(data);
  }, [getFilterYearListData.data]); 

  // 설비명 필터 초기값 설정
  useEffect(() => {
    // 데이터가 로드되지 않았거나 비어 있는 경우 처리 중단
    if(!getFilterMachineListData.data || getFilterMachineListData.data.length === 0) {
      return;
    }
    if(!getFilterYearListData.data || getFilterYearListData.data.length === 0) {
      return;
    }
    
    if(searchMachineStatus.hasOwnProperty('machine_id')) {
      return;
    }

    // 첫 번째 설비명 데이터 가져오기
    const machineId = getFilterMachineListData.data[0].machine_id;

    const data = {
      machine_id: machineId,
    };
    
    setSearchMachineStatus(data);
  }, [getFilterMachineListData.data, getFilterYearListData.data]); 

  // 렌더 상태 관리
  // 렌더 상태값: none(렌더링 x), render(렌더링)
  const [renderFSM, setRenderFSM] = useState('none');
  const handleRenderChanged = (newValue) => setRenderFSM(newValue);

  // 데이터 조회 시, 데이터가 변경될 때마다 데이터를 갱신합니다.
  useEffect(() => {
    if(!getRegisterCauseData.data || getRegisterCauseData.data.length === 0 ) {
      return;
    }
    if(!getEvaluationData.data || getEvaluationData.data.length === 0) {
      return;
    }
    if(!getMachineData.data || getMachineData.data.length === 0) {
      return;
    }

    handleRenderChanged('render');
  },[getRegisterCauseData.data, getEvaluationData.data, getMachineData.data]);

  ////////////////////////
  // Helper Functions
  ////////////////////////

  // 선택한 평가연도의 위험요인 지표 데이터 가져오기
  const getSelectedYearRegisterCauseData = (year) => {
    const data = getRegisterCauseData.data.find(item => item.year === year);
    return data;
  };

  // 위험요인 총 건수 전년대비 계산
  const getCalculationYearOverYear = (currentYear, previousYear) => {
    const currentTotalCount = getSelectedYearRegisterCauseData(currentYear)?.register_cause_total_count;
    const previousTotalCount = getSelectedYearRegisterCauseData(previousYear)?.register_cause_total_count;

    // 전년 데이터가 없으면 0으로 처리
    // if(!previousTotalCount || previousTotalCount === 0) {
    //   return 0;
    // }

    const result = currentTotalCount - previousTotalCount;
    return result;
  };

  // 선택한 평가연도의 위험성 평가완료 지표 데이터 가져오기
  const getSelectedYearEvalData = (year) => {
    const data = getEvaluationData.data.find(item => item.year === year);
    return data;
  };
  
  ////////////////////////
  // 렌더링
  ////////////////////////

  // 평가연도 목록 (중복 제거를 위한 작업)
  const yearList = Array.from(
    new Set(getFilterYearListData.data.map(item => item.eval_year))
  );

  // 설비명 목록 (중복 제거를 위한 작업)
  const machineIdList = Array.from(
    new Map(
      getFilterMachineListData.data.map((item) => [
        item.machine_id, 
        { 
          machine_id: item.machine_id, 
          machine_name: item.machine_name 
        }
      ])
    ).values()
  );

  // 위험요인 총 건수
  const registerCauseTotalCount = getSelectedYearRegisterCauseData(searchYearStatus.year)?.register_cause_total_count;
  // 전년 대비 (선택한 평가연도 기준, 전년 위험요인 총 건수 차이)
  const yearOverYearRegisterCause = getCalculationYearOverYear(searchYearStatus.year, (searchYearStatus.year - 1));

  // 설비 종합 위험성
  const machineRiskLevel = getMachineData.data?.machine_total_risk?.monthly_risk_level;
  const riskLevelAssessment = getMachineData.data?.machine_total_risk?.risk_level_assessment || '-';
  const yearOverYearRisk = getMachineData.data?.machine_total_risk?.year_over_year_risk;
  const monthOverMonthRisk = getMachineData.data?.machine_total_risk?.month_over_month_risk;
  const monthlyTotalData = getMachineData.data?.monthly_risks;

  ////////////////////////
  // 렌더링
  ////////////////////////
  function renderData() {
    return (
      <TableContainer>
        {/* 평가연도 필터 */}
        <FormControl size="small">
          <Box sx={{ display: "flex", alignItems: "center",gap: '16px', mb: '16px'}}>
            <Typography sx={{fontWeight: FONT_PROPS.SEMIBOLD,fontSize: 16,color: theme.text,}}>
              평가연도
            </Typography>
            <Select
              value={searchYearStatus.year || ""}
              displayEmpty
              onChange={(e) => {
                setSearchYearStatus({
                  ...searchYearStatus,
                  year: e.target.value
                });
              }}
              sx={{ width: "100px",fontWeight: FONT_PROPS.SEMIBOLD,color: theme.text,fontSize: 15,}}
            >
              {yearList.reverse().map((year, index) => (
                <MenuItem
                  key={index}
                  value={year}
                  sx={{ fontWeight: FONT_PROPS.SEMIBOLD,color: theme.text,fontSize: 15,}}
                >
                  {year}년
                </MenuItem>
              ))}
            </Select>
          </Box>
        </FormControl>

        <CollapsibleCardTemplate title="유해·위험요인 지표" isInitialOpen={true}>
          {/* 위험요인 총 건수 */}
          <Card 
            sx={{ 
              width: '350px',
              height: 'auto',
              boxShadow: '0px 1px 4px rgba(0,0,0,0.07)',
              border: `1px solid ${theme.palette.divider}`, 
              borderRadius: "0.375rem", 
              bgcolor: 'var(--mui-palette-background-paper)',
              mb: 4,
            }}>
            <CardHeader sx={{ padding: '20px 30px' }}
              title={
                <Typography sx={{ fontWeight: FONT_PROPS.SEMIBOLD, fontSize: 16, }}>
                  유해·위험요인 총 건수
                </Typography>
              }
            />
            <CardContent sx ={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0px 30px 20px 30px !important' }}>
              <Typography sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 36, }}>
                {registerCauseTotalCount}
              </Typography>
              
              <Box sx = {{ display: 'flex', alignItems: 'center' }}>
                {/* 화살표 방향, 상향(45도), 하향(225도), 상향 색상: #EB1515 , 하향 색상: #21CC40 , 유지 색상: #C8C8DA */}  
                {yearOverYearRegisterCause > 0 && 
                  <>
                    <ArrowUpwardIcon sx={{ transform: 'rotate(45deg)', color: '#EB1515' }} /> 
                    <Typography
                    sx={{
                      fontWeight: FONT_PROPS.MEDIUM,
                      fontSize: 16,
                      color: '#EB1515',
                    }}
                    >
                      {yearOverYearRegisterCause}
                    </Typography>
                  </>
                }
                {yearOverYearRegisterCause === 0 && 
                  <>
                    <RemoveIcon sx={{ color: '#C8C8DA'}} />
                    <Typography
                      sx={{
                        fontWeight: FONT_PROPS.MEDIUM,
                        fontSize: 16,
                        color: '#C8C8DA',
                      }}
                    >
                      {yearOverYearRegisterCause}
                    </Typography>
                  </>
                }
                {yearOverYearRegisterCause < 0 && 
                  <>
                    <ArrowUpwardIcon sx={{ transform: 'rotate(225deg)', color: '#21CC40' }} /> 
                    <Typography
                      sx={{
                        fontWeight: FONT_PROPS.MEDIUM,
                        fontSize: 16,
                        color: '#21CC40',
                      }}
                    >
                      {Math.abs(yearOverYearRegisterCause)}
                    </Typography>
                  </>
                }
                <Typography
                  sx={{
                    fontWeight: FONT_PROPS.MEDIUM,
                    fontSize: 16,
                    marginLeft: '8px',
                  }}
                >
                  전년 대비
                </Typography>
              </Box>
            </CardContent>
          </Card>
          {/* 차트 */}
          <Box sx = {{display: 'flex', gap: 4 }}>
            {/* 위험요인 건수 추이 차트 */}
            <MonthlyTrendRegisterCauseChart chartData={getSelectedYearRegisterCauseData(searchYearStatus.year)} />
            {/* 위험요인 건수 3년 추이 */}
            <Card
              sx={{ 
                width: '100%',
                height: 'auto',
                //mx: 'auto',
                boxShadow: '0px 1px 4px rgba(0,0,0,0.07)',
                border: `1px solid ${theme.palette.divider}`, 
                borderRadius: "0.375rem", 
                bgcolor: 'var(--mui-palette-background-paper)',
                //overflow: 'hidden'
              }}
            >
              <Typography 
                sx={{
                  fontFamily: FONT_PROPS.PRIMARY,
                  fontWeight: FONT_PROPS.SEMIBOLD,
                  fontSize: 16,
                  padding: '24px 24px',
                }}>
                유해·위험요인 건수 3년 추이
              </Typography>
              <Grid container spacing={2} justifyContent="center" sx={{ marginTop: '40px' }}>
                <Grid item>
                  <ThreeYearTrendRegisterCauseChart chartData={getSelectedYearRegisterCauseData(searchYearStatus.year - 2)} />
                </Grid>
                <Grid item>
                  <ThreeYearTrendRegisterCauseChart chartData={getSelectedYearRegisterCauseData(searchYearStatus.year - 1)} />
                </Grid>
                <Grid item>
                  <ThreeYearTrendRegisterCauseChart chartData={getSelectedYearRegisterCauseData(searchYearStatus.year)} />
                </Grid>
              </Grid>
            </Card>
          </Box>
        </CollapsibleCardTemplate>

        <CollapsibleCardTemplate title="위험성평가 완료 지표" isInitialOpen={true}>
          <Box sx = {{display: 'flex', gap: 4 }}>
            {/* 1 ~ 12월 평가완료 지표*/}
            <MonthlyEvaluationLevelGrid cardData={getSelectedYearEvalData(searchYearStatus.year)} />
            {/* 평가율 3년 추이 차트 */}
            <Card
              sx={{ 
                width: '100%',
                height: 'auto',
                //mx: 'auto',
                boxShadow: '0px 1px 4px rgba(0,0,0,0.07)',
                border: `1px solid ${theme.palette.divider}`, 
                borderRadius: "0.375rem", 
                bgcolor: 'var(--mui-palette-background-paper)',
                //overflow: 'hidden'
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: FONT_PROPS.SEMIBOLD, fontSize: 16, padding: '24px' }}>평가율 3년 추이</Typography>
                <Grid container spacing={3} justifyContent="center" sx={{ marginTop: '40px'}}>
                  <Grid item><EvaluationRateDonut chartData={getSelectedYearEvalData(searchYearStatus.year - 2)} /></Grid>
                  <Grid item><EvaluationRateDonut chartData={getSelectedYearEvalData(searchYearStatus.year - 1)} /></Grid>
                  <Grid item><EvaluationRateDonut chartData={getSelectedYearEvalData(searchYearStatus.year)} /></Grid>
                </Grid>
                <Box mt={2} display="flex" justifyContent="center" alignItems="center" gap={2} sx={{ padding: '40px 20px' }}>
                  <Box width={10} height={10} borderRadius="50%" bgcolor="#6287DB" />
                  <Typography sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 14 }}>평가율</Typography>
                </Box>
              </Box>
            </Card>
          </Box>
        </CollapsibleCardTemplate>

        <CollapsibleCardTemplate title="설비별 종합 위험성" isInitialOpen={true}>
          {/* 설비명 필터 */}
          <FormControl size="small">
            <Box sx={{ display: "flex", alignItems: "center", gap: '16px', marginBottom: '16px'}}>
              <Typography sx={{fontWeight: FONT_PROPS.SEMIBOLD, fontSize: 16, color: theme.text,}}>
                설비명
              </Typography>
              <Select
                value={(machineIdList.length !== 0 && searchMachineStatus.machine_id) || ""} // 평가연도 변경 시 설비명 조회 데이터가 0인 경우가 있어 이렇게 처리함
                displayEmpty
                onChange={(e) => {
                    setSearchMachineStatus({
                    ...searchMachineStatus,
                    machine_id: e.target.value
                  });
                }}
                sx={{
                  minWidth: "120px",
                  fontWeight: FONT_PROPS.SEMIBOLD,
                  color: FONT_PROPS.DARK,
                  fontSize: 15,
                }}
              >
                {machineIdList.map((data, index) => {
                  const idField = data.hasOwnProperty("machine_id") ? "machine_id" : "";
                  return (
                    <MenuItem
                      key={index}
                      value={data[idField]}
                      sx={{
                        fontWeight: FONT_PROPS.SEMIBOLD,
                        color: theme.text,
                        fontSize: 15,
                      }}
                    >
                      {data.machine_name}
                    </MenuItem>
                  );
                })}
              </Select>
            </Box>
          </FormControl>
          {/* 설비별 종합 위험성 */}
          <Card
            sx={{
              width: '350px',
              height: 'auto',
              boxShadow: '0px 1px 4px rgba(0,0,0,0.07)',
              border: `1px solid ${theme.palette.divider}`, 
              borderRadius: "0.375rem", 
              bgcolor: 'var(--mui-palette-background-paper)',
              mb: 4,
            }}>
            <CardHeader sx={{ padding: '20px 30px' }}
              title={
                <Typography
                  sx={{
                    fontWeight: FONT_PROPS.SEMIBOLD,
                    fontSize: 16,
                  }}
                >
                  설비 종합 위험성
                </Typography>
              }
            />
            <CardContent sx ={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0px 30px 20px 30px !important' }}>
              <Typography
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontWeight: FONT_PROPS.MEDIUM,
                  fontSize: 36,
                }}
              >
                {machineRiskLevel}
                <Typography component="span" fontSize={20} fontWeight={FONT_PROPS.REGULAR}>({riskLevelAssessment})</Typography>
              </Typography>
              <Box mt={-6} gap={1} sx={{ display: 'flex', flexDirection: 'column'}}>
                {/* 전년 동월 대비 */}
                <Box sx = {{ display: 'flex', alignItems: 'center' }}> 
                  {/* 화살표 방향, 상향(45도), 하향(225도), 상향 색상: #EB1515 , 하향 색상: #21CC40 , 유지 색상: #C8C8DA */}  
                  {yearOverYearRisk > 0 && 
                    <>
                      <ArrowUpwardIcon sx={{ transform: 'rotate(45deg)', color: '#EB1515' }} /> 
                      <Typography
                      sx={{
                        fontWeight: FONT_PROPS.MEDIUM,
                        fontSize: 16,
                        color: '#EB1515',
                      }}
                      >
                        {yearOverYearRisk}
                      </Typography>
                    </>
                  }
                  {yearOverYearRisk === 0 && 
                    <>
                      <RemoveIcon sx={{ color: '#C8C8DA'}} />
                      <Typography
                        sx={{
                          fontWeight: FONT_PROPS.MEDIUM,
                          fontSize: 16,
                          color: '#C8C8DA',
                        }}
                      >
                        {yearOverYearRisk}
                      </Typography>
                    </>
                  }
                  {yearOverYearRisk < 0 && 
                    <>
                      <ArrowUpwardIcon sx={{ transform: 'rotate(225deg)', color: '#21CC40' }} /> 
                      <Typography
                        sx={{
                          fontWeight: FONT_PROPS.MEDIUM,
                          fontSize: 16,
                          color: '#21CC40',
                        }}
                      >
                        {Math.abs(yearOverYearRisk)}
                      </Typography>
                    </>
                  }
                  <Typography
                    sx={{
                      fontWeight: FONT_PROPS.MEDIUM,
                      fontSize: 16,
                      marginLeft: '8px',
                    }}
                  >
                    전년 동월 대비
                  </Typography>
                </Box>
                {/* 전월 대비 */}
                <Box sx = {{ display: 'flex', alignItems: 'center' }}> 
                  {/* 화살표 방향, 상향(45도), 하향(225도), 상향 색상: #EB1515 , 하향 색상: #21CC40 , 유지 색상: #C8C8DA */}  
                  {monthOverMonthRisk > 0 && 
                    <>
                      <ArrowUpwardIcon sx={{ transform: 'rotate(45deg)', color: '#EB1515' }} /> 
                      <Typography
                      sx={{
                        fontWeight: FONT_PROPS.MEDIUM,
                        fontSize: 16,
                        color: '#EB1515',
                      }}
                      >
                        {monthOverMonthRisk}
                      </Typography>
                    </>
                  }
                  {monthOverMonthRisk === 0 && 
                    <>
                      <RemoveIcon sx={{ color: '#C8C8DA'}} />
                      <Typography
                        sx={{
                          fontWeight: FONT_PROPS.MEDIUM,
                          fontSize: 16,
                          color: '#C8C8DA',
                        }}
                      >
                        {monthOverMonthRisk}
                      </Typography>
                    </>
                  }
                  {monthOverMonthRisk < 0 && 
                    <>
                      <ArrowUpwardIcon sx={{ transform: 'rotate(225deg)', color: '#21CC40' }} /> 
                      <Typography
                        sx={{
                          fontWeight: FONT_PROPS.MEDIUM,
                          fontSize: 16,
                          color: '#21CC40',
                        }}
                      >
                        {Math.abs(monthOverMonthRisk)}
                      </Typography>
                    </>
                  }
                  <Typography
                    sx={{
                      fontWeight: FONT_PROPS.MEDIUM,
                      fontSize: 16,
                      marginLeft: '8px',
                    }}
                  >
                    전월 대비
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          {/* 차트 */}
          <Box sx = {{display: 'flex', gap: 4 }}>
            {/* 월별 종합 위험성 추이 차트 */}
            <MonthlyRiskTotalChart chartData={monthlyTotalData} />
            {/* 월별 상.중.하 추이 차트 */}
            <MonthlyRiskEvaluationChart chartData={monthlyTotalData} />
          </Box>
        </CollapsibleCardTemplate>

      </TableContainer>
    );
  }

  // MonitoringResult 렌더링
  return (
    <Box sx={{ bgcolor: 'var(--mui-palette-background-paper)',height: '100%',overflowY: 'auto',display: 'flex',flexDirection: 'column'}}>
      {/* 브레드크럼 + 타이틀 */}
      <BreadcrumbsTable parentTitle={PARENT_TITLE} childTitle={CHILD_TITLE} />
      {/* 테이블 표시 */}
      {renderData()}
    </Box>
  );
}

// 월별 위험요인 건수 추이 차트
const MonthlyTrendRegisterCauseChart = ({ chartData }) => {
  const theme = useTheme();
  const chartRef = useRef(null);

  useEffect(() => {
    if(!chartData || chartData.length === 0) {
      return;
    }

    const myChart = echarts.init(chartRef.current);

    const option = {
      title: { // 타이틀 설정
        text: '월별 유해·위험요인 건수 추이',
        left: 'left', // 왼쪽 정렬
        //top: 'top',   // 필요시 위치 조정 가능
        textStyle: {
          fontFamily: FONT_PROPS.PRIMARY,
          fontWeight: FONT_PROPS.SEMIBOLD,
          fontSize: 16,
          //textAlign: 'left' // 필요하면 추가
        }
      },
      tooltip: { // 툴팁 설정
        trigger: 'axis',
      },
      legend: { // 범례 설정
        data: ['유해·위험요인 건수', '고위험 건수'],
        bottom: 0,
        textStyle: {
          fontFamily: FONT_PROPS.PRIMARY,
          fontWeight: FONT_PROPS.MEDIUM,
          fontSize: 12,
        },
      },
      grid: { // 그리드 설정
        left: '2%',
        right: '2%',
        bottom: '10%',
        containLabel: true,
      },
      xAxis: { // x축 설정
        type: 'category',
        boundaryGap: false,
        data: ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'],
        axisLabel: { // x축 레이블 설정
          fontFamily: FONT_PROPS.PRIMARY, // x축 폰트 패밀리
          fontWeight: FONT_PROPS.MEDIUM, // x축 폰트 굵기
          fontSize: 14,
        },
      },
      yAxis: { // y축 설정
        type: 'value',
        axisLabel: {
          fontFamily: FONT_PROPS.PRIMARY, // x축 폰트 패밀리
          fontWeight: FONT_PROPS.MEDIUM, // x축 폰트 굵기
          fontSize: 14,
        },
      },
      series: [ // 그래프 설정
        {
          name: '유해·위험요인 건수',
          type: 'line',
          //stack: 'Total', // 그래프 스택으로 쌓을 지 여부
          symbol: 'none', // 포인트 제거
          areaStyle: {
            opacity: 0.2, // 연한 영역 색
          },
          itemStyle: {
            color: '#B3CDE3',
          },
          data: chartData.monthly_register_cause_count,
          //data: [50, 120, 150, 160, 180, 220, 280, 310, 320, 330, 340, 360],
        },
        {
          name: '고위험 건수',
          type: 'line',
          //stack: 'Total', // 그래프 스택으로 쌓을 지 여부
          symbol: 'none', // 포인트 제거
          areaStyle: {
            opacity: 0.2, // 연한 영역 색
          },
          itemStyle: {
            color: '#FBB4AE',
          },
          data: chartData.monthly_high_risk_count,
          //data: [10, 40, 80, 100, 120, 150, 160, 170, 180, 185, 190, 200],
        },
      ],
    };

    myChart.setOption(option);

    // resize 대응
    window.addEventListener('resize', () => myChart.resize());

    return () => {
      myChart.dispose();
    };
  }, [chartData]);

  return (
    <Card
      sx={{ 
        width: '100%',
        height: 'auto',
        //mx: 'auto',
        boxShadow: '0px 1px 4px rgba(0,0,0,0.07)',
        border: `1px solid ${theme.palette.divider}`, 
        borderRadius: "0.375rem", 
        bgcolor: 'var(--mui-palette-background-paper)',
        //overflow: 'hidden'
      }}
    >
      <CardContent>
        <div ref={chartRef} style={{ height: '400px', width: '100%' }} />
      </CardContent>
    </Card>
  );
};

// 위험요인 건수 3년 추이 차트 (부분 도넛 차트)
const ThreeYearTrendRegisterCauseChart = ({ chartData }) => {
  //const theme = useTheme();
  const chartRef = useRef(null);

  useEffect(() => {
    if(!chartData || chartData.length === 0) {
      return;
    }

    const chart = echarts.init(chartRef.current);

    const dummyValue = (chartData.high_risk_count + chartData.row_risk_count) * 0.25; // 빈공간을 위한 더미 값

    // 고위험 건수와 위험 건수를 각각의 데이터로 설정
    const realData = [
      { 
        value: chartData.high_risk_count,
        name: '고위험 건수',
        itemStyle: 
        { 
          color: '#FDCDAC',
          borderWidth: 2,
          borderColor: '#fff',
          borderRadius: 4,
        }
      },
      { 
        value: chartData.row_risk_count,
        name: '위험 건수',
        itemStyle:
        { 
          color: '#B3E2CD',
          borderWidth: 2,
          borderColor: '#fff',
          borderRadius: 4,
        } 
      },
    ];
    // 빈공간을 위한 더미 데이터
    const dummyData = {
      value: dummyValue,
      name: '빈공간',
      itemStyle: 
      { 
        color: 'transparent'
      }, 
      tooltip: { show: false }, label: { show: false } 
    };
  
    // 총 건수 계산
    const total = realData.reduce((sum, d) => sum + d.value, 0);
    // 비율 계산
    //const highRiskPercent = ((chartData.high_risk_count / total) * 100).toFixed(1);
    //const normalRiskPercent = ((chartData.row_risk_count / total) * 100).toFixed(1);

    // 차트 옵션 설정
    const option = {
      title: {
        text: total,
        left: 'center',
        top: '45%',
        textStyle: {
          fontSize: 36,
          fontWeight: FONT_PROPS.SEMIBOLD,
          align: 'center'
        }
      },
      tooltip: {
        trigger: 'item',
        //formatter: '{b}: {c}건 ({d}%)'
        formatter: function (params) {
          // 빈공간 제외
          const found = realData.find(d => d.name === params.name);
          if (!found) return ''; // 빈공간이면 표시 안함

          const percent = ((params.value / total) * 100).toFixed(1);

          return `
            <div>
              <strong>${params.name}</strong>
              <br/>건수: ${params.value}<br/>
              비율: ${percent}%
            </div>`;
        },
        // 툴팁 스타일
        // extraCssText: `
        //   padding:10px;
        //   background:#fff;
        //   border:1px solid ${theme.palette.divider};
        //   color:${theme.palette.text.primary};
        //   font-family:${FONT_PROPS.PRIMARY};
        //   border-radius:8px;
        //   `,
      },
      series: [
        {
          name: '건수',
          type: 'pie',
          radius: ['60%', '85%'], // 도넛 크기 (안쪽, 바깥쪽)
          center: ['50%', '50%'], // 차트 중앙
          startAngle: 235, // 어디부터 시작할지 조절
          //label: { show: false },
          label: {
            show: true,
            position: 'inner', // 또는 'center', 'inner', 'outside'
            formatter: function (params) {
              return params.value === 0 ? '' : params.value;
            },
            //formatter: '{c}',   // '{c}' = value, '{b}' = name, '{d}%' = percent
            fontSize: 12,
            fontWeight: FONT_PROPS.MEDIUM,
            fontFamily: FONT_PROPS.PRIMARY,
            //color: '#333'
          },
          labelLine: { show: false },
          // data: [
          //   { 
          //     value: highRisk,
          //     name: '고위험 건수',
          //     itemStyle: 
          //     { 
          //       color: '#FDCDAC',
          //       borderWidth: 2,
          //       borderColor: '#fff',
          //       borderRadius: 4,
          //     }
          //   },
          //   { 
          //     value: normalRisk,
          //     name: '위험 건수',
          //     itemStyle:
          //     { 
          //       color: '#B3E2CD',
          //       borderWidth: 2,
          //       borderColor: '#fff',
          //       borderRadius: 4,
          //     } 
          //   },
          //   { 
          //     value: dummyValue,
          //     name: '빈공간',
          //     itemStyle: 
          //     { 
          //       color: 'transparent'
          //     }, 
          //     tooltip: { show: false }, label: { show: false } 
          //   }
          // ]
          data: [ ...realData, dummyData ],
        }
      ]
    };

    chart.setOption(option);
    return () => chart.dispose();
  }, [chartData]);

  return(
    <Box sx={{ textAlign: 'center' }}>
      {/* 도넛차트 */}
      <div ref={chartRef} style={{ width: 200, height: 200 }} />
      <Box sx={{ transform: 'translateY(-50px)'}}>
        {/* 연도 */}
        <Typography 
          variant="subtitle1" sx={{ fontWeight: FONT_PROPS.SEMIBOLD, fontSize: 18, marginBottom: 6 }}>{chartData?.year}년</Typography>
        {/* 위험 건수 */}
        <Box display="flex" justifyContent="center" mt={1} gap={6}>
          <Box display="flex" alignItems="center" mr={2} >
            <Box sx={{ width: 10, height: 10, bgcolor: '#fbc4ab', borderRadius: '50%', mr: 1.5 }} />
            <Typography variant="caption">고위험 건수</Typography>
          </Box>
          <Box display="flex" alignItems="center" >
            <Box sx={{ width: 10, height: 10, bgcolor: '#b9e4c9', borderRadius: '50%', mr: 1.5 }} />
            <Typography variant="caption">위험 건수</Typography>
          </Box>
        </Box>
        {/* 위험 건수 퍼센테이지 */}
        <Box display="flex" justifyContent="center" mt={0.5} gap={12} sx={{ fontWeight: FONT_PROPS.SEMIBOLD, fontSize: 13 }}>
          <Typography variant="caption" mr={2} sx={{fontWeight: 'inherit', fontSize: 'inherit'}} >{chartData?.high_risk_rate}%</Typography>
          <Typography variant="caption" sx={{fontWeight: 'inherit', fontSize: 'inherit'}}>{chartData?.row_risk_rate}%</Typography>
        </Box>
      </Box>
      {/* 총 건수 */}
      <Typography 
        sx={{
          display: 'inline-block',     // ✅ 줄에 맞춰 크기 조절
          width: 'fit-content',        // ✅ 글자 너비만큼 자동 조절
          height: 'fit-content',       // ✅ 높이도 텍스트에 맞게
          transform: 'translateY(-230px)',
          fontFamily: FONT_PROPS.PRIMARY,
          fontWeight: FONT_PROPS.SEMIBOLD,
          fontSize: 16,
        }}>
        총 건수
      </Typography>
    </Box>
  ); 
};

// 월별 평가완료 지표
const MonthlyEvaluationLevelGrid = ({ cardData }) => {

  const monthData = [];
  for(let i = 0; i < 12; ++i) {
    monthData.push({ 
      month: i + 1, 
      count: cardData?.monthly_register_cause_count[i],
      alert: cardData?.monthly_uncompleted_count[i],
      alert_info: cardData?.monthly_uncompleted_info[i]
    });
  }
  // const monthData = [
  //   { month: 1, count: 12, alert: 3 },
  //   { month: 2, count: 12, alert: 0 },
  //   { month: 3, count: 12, alert: 3 },
  //   { month: 4, count: 12, alert: 3 },
  //   { month: 5, count: 12, alert: 3 },
  //   { month: 6, count: 12, alert: 3 },
  //   { month: 7, count: 12, alert: 3 },
  //   { month: 8, count: 12, alert: 0 },
  //   { month: 9, count: 12, alert: 3 },
  //   { month: 10, count: 12, alert: 0 },
  //   { month: 11, count: 12, alert: 3 },
  //   { month: 12, count: 12, alert: 3 },
  // ];

  return (
    <Box sx={{ maxWidth: '50%', mx: 'auto' }}>
      <Grid container spacing={2} >
        {monthData.map((item) => (
          <Grid item xs={12} sm={6} md={4} key={item.month}>
            <EvaluationCardTemplate {...item} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
// 위험성 평가완료 카드
const EvaluationCardTemplate = ({ month, count, alert, alert_info }) => {
  const theme = useTheme();
  const [evalModal, setEvalModal] = useState(false);
  const handleEvalModal = () => setEvalModal(!evalModal);

  const isComplete = alert === 0;
  const isPending = alert === -1;
  const isIncomplete = !isComplete && !isPending;

  // 테이블 참조
  // const tableRef = useRef();
  // const clearSelection = () => { tableRef.current?.clearSelection(); };
  // const getSelectedRows = () => { return tableRef.current?.getSelectedRows(); };

  // 테이블의 상단 툴바 커스터마이징 관련 내용
  const [showSearch, setShowSearch] = useState(false); // 검색창 열림 여부
  const [globalFilter, setGlobalFilter] = useState(''); // 검색 텍스트 상태
  const customTableTopToolbar = (table) => {
    return(
    <Box width="100%" display="flex" justifyContent="space-between" alignItems="center" p={2}>
      {/* 왼쪽 영역 */}
      <Box display="flex" alignItems="center" gap={2}>
        {/* TODO: */}
      </Box>

      {/* 오른쪽 영역 - 기존 내부 액션도 원하면 여기에 직접 렌더링 */}
      <Box display="flex" alignItems="center" gap={2}>
        {/* 검색창 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => setShowSearch((prev) => !prev)}>
            <SearchIcon />
          </IconButton>
        
          <Collapse in={showSearch} orientation="horizontal">
          <TextField
            size="small"
            placeholder="검색"
            value={globalFilter}
            onChange={(e) => {
              setGlobalFilter(e.target.value);
              table.setGlobalFilter(e.target.value);
            }}
            sx={{ minWidth: 200 }}
            variant="outlined"
            slots={{
              input: OutlinedInput,
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setGlobalFilter('');
                        table.setGlobalFilter('');
                        setShowSearch(false); // 닫기
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }
            }}
          />
          </Collapse>
        </Box>
      </Box>
    </Box>
    );
  };

  return (
    <>
      <Card 
        sx={{
          width: '250px',
          height: '120px',
          boxShadow: '0px 1px 4px rgba(0,0,0,0.07)',
          border: `1px solid ${theme.palette.divider}`, 
          borderRadius: "0.375rem", 
          bgcolor: 'var(--mui-palette-background-paper)',
        }}
      >
        <CardContent sx={{ padding: '20px 30px' }}>
          {/* 매월 */}
          <Typography sx={{ fontWeight:FONT_PROPS.SEMIBOLD, fontSize: 16 }}>
            {month}월
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            {/* 매월 위험요인 등록 수 */}
            <Typography sx={{ fontWeight:FONT_PROPS.SEMIBOLD, fontSize: 36 }}>
              {count}
            </Typography>
            {/* 평가표시 */}
            <Box mt={1} display="flex" justifyContent="center" alignItems="center" gap={1}>
              {isComplete && (
                <>
                  <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'green' }} />
                  <Typography fontSize={14} sx={{ color: 'green' }}>
                    평가 완료
                  </Typography>
                </>
              )}
              {isPending && (
                <>
                  <RemoveIcon fontSize="small" sx={{ color: '#C8C8DA' }} />
                  <Typography fontSize={14} sx={{ color: '#C8C8DA' }}>
                    평가 대기
                  </Typography>
                </>
              )}
              {isIncomplete && (
                <>
                  <NotificationsActiveIcon fontSize="small" sx={{ color: 'red' }} />
                  <Typography fontSize={14} sx={{ color: 'red' }}>
                    평가 미완료
                  </Typography>
                </>
              )}
            </Box>
            {isIncomplete && (
              <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <Tooltip title="상세보기">
                  <Typography 
                    fontSize={12} 
                    mt={0.5} 
                    sx={{ 
                      color: 'red',
                      display: 'inline-block',
                      paddingX: 1,
                      paddingY: 1,
                      borderRadius: 1,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.2)',
                      },
                    }}
                    onClick={handleEvalModal}
                  >
                    🔔 {alert}
                  </Typography>
                </Tooltip>
              </Box>
            )}
            
            {isPending && (
              <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <Typography 
                  fontSize={12} 
                  mt={0.5} 
                  sx={{ 
                    color: 'red',
                    // display: 'inline-block',
                    // paddingX: 1,
                    // paddingY: 1,
                    // borderRadius: 1,
                    // cursor: 'pointer',
                    // transition: 'all 0.2s ease',
                    // '&:hover': {
                    //   backgroundColor: theme.palette.action.hover,
                    //   boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.2)',
                    // },
                  }}
                  // onClick={handleUseModal}
                >
                  {/* 🔔 {alert} */}
                </Typography>
              </Box>
            )}
            {isComplete && (
              <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <Typography 
                  fontSize={12}
                  mt={0.5}
                  sx={{
                    color: 'green',
                    // display: 'inline-block',
                    // paddingX: 1,
                    // paddingY: 1,
                    // borderRadius: 1,
                    // cursor: 'pointer',
                    // transition: 'all 0.2s ease',
                    // '&:hover': {
                    //   backgroundColor: theme.palette.action.hover,
                    //   boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.2)',
                    // },
                  }}
                  //onClick={handleUseModal}
                >
                  {/* 🔔 {alert} */}
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
      {/* 평가 미완료 상세정보 모달 */}
      <ModalTemplate 
        isOpen={evalModal}
        handleConfirm={handleEvalModal}
        handleCancel={handleEvalModal}
        confirmText="확인"
        enableSingleActionButton={true}
        title={`${month}월 위험성평가 미완료 상세정보`}
        containerSX={{ width: 'auto', height: 'auto' }}
      >
        <NewTableTemplate
          columns={monitoringResultMonthlyInfoColumns}
          data={(alert_info && alert_info.data) || []}
          topToolbarContent={customTableTopToolbar}
          enableEditing={false}
          enableCustomGlobalFilter={true}
          enabledSelectionRow={false}
          enabledSelectionAllRow={false}
          templateSX ={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "0.375rem", 
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)',
          }}
        />
      </ModalTemplate >
    </>
  );
};

// 위험성 평가율 3년 추이 차트 (도넛 차트)
const EvaluationRateDonut = ({ chartData }) => {
  const chartRef = useRef(null);
  const remaining = 100 - (chartData?.eval_percent || 0);

  useEffect(() => {
    const chart = echarts.init(chartRef.current);

    const option = {
      tooltip: { show: false },
      legend: { show: false },
      series: [
        {
          type: 'pie',
          radius: ['70%', '90%'],
          center: ['50%', '50%'],
          startAngle: 90,
          label: {
            show: true,
            position: 'center',
            formatter: `${chartData?.eval_percent || 0}%`,
            fontSize: 36,
            fontWeight: FONT_PROPS.SEMIBOLD,
            //color: '#333'
          },
          labelLine: { show: false },
          data: [
            {
              value: chartData?.eval_percent || 0,
              name: '평가율',
              itemStyle: {
                color: '#6287DB'
              }
            },
            {
              value: remaining,
              name: '나머지',
              itemStyle: {
                color: '#dee6f6'
              },
              tooltip: { show: false }
            }
          ]
        }
      ]
    };

    chart.setOption(option);
    return () => chart.dispose();
  }, [chartData]);

  return (
    <Box textAlign="center">
      <div ref={chartRef} style={{ width: 200, height: 200 }} />
      <Typography sx={{ fontWeight: FONT_PROPS.SEMIBOLD,  fontSize: 18, marginTop: '12px' }}>{chartData?.year || 0}년</Typography>
    </Box>
  );
};

// 월별 종합 위험성 추이 차트(가로 스택 차트)
const MonthlyRiskTotalChart = ({ chartData }) => {
  const theme = useTheme();
  const chartRef = useRef(null);

  /**
   * none, 1월 ~ 12월
   */
  // const [selectedMonth, setSelectedMonth] = useState('none');
  function createDiagonalPattern() {
    const canvas = document.createElement('canvas');
    canvas.width = 10;
    canvas.height = 10;
    const ctx = canvas.getContext('2d');
  
    //ctx.strokeStyle = '#999';
    ctx.strokeStyle = '#B8B8CA';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, 10);
    ctx.lineTo(10, 0);
    ctx.stroke();
  
    return canvas;
  }

  useEffect(() => {
    const chart = echarts.init(chartRef.current);
 
    // 월별 데이터
    const months = chartData?.months.map(item => { return `${item}월`}); 
    
    const levelColors = {
      매우낮음: '#D5FFD5',
      낮음: '#B8FFB8',
      보통: '#FBFF00',
      약간높음: '#FFD5B8',
      높음: '#FF9900',
      매우높음: '#FF4444',
      미평가: {
        image: createDiagonalPattern(), // 빗금 무늬
        repeat: 'repeat'
      }
    };
  
    // 월별로 랜덤한 위험 단계 값
    const levelData = {
      매우낮음: chartData?.total_risks.very_low,
      낮음: chartData?.total_risks.low,
      보통: chartData?.total_risks.normal,
      약간높음: chartData?.total_risks.slightly_high,
      높음: chartData?.total_risks.high,
      매우높음: chartData?.total_risks.very_high,
      미평가: chartData?.total_risks.not_evaluated,
    };
  
    const series = Object.keys(levelData).map(level => ({
      name: level,
      type: 'bar',
      stack: 'total',
      label: {
        show: true,
        position: 'inside',
        formatter: function (params) {
          return params.value === 0 ? '' : params.value; // 0은 표시 안함
        },
        color: '#505050',
        fontWeight: FONT_PROPS.SEMIBOLD,
        fontSize: 12,
      },
      itemStyle: {
        color: levelColors[level]
      },
      // emphasis: {
      //   focus: 'self', // ✅ 핵심: 해당 막대 조각만 강조됨
      //   itemStyle: {
      //     shadowColor: 'rgba(0, 0, 0, 0.2)',
      //     shadowBlur: 10,
      //     borderWidth: 1,
      //     borderColor: '#666'
      //   }
      // },
      data: levelData[level]
    }));
  
    const option = {
      title: {
        text: '월별 종합 위험성 추이',
        left: 'left',
        textStyle: {
          fontFamily: FONT_PROPS.PRIMARY,
          fontWeight: FONT_PROPS.SEMIBOLD,
          fontSize: 16,
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' }
      },
      legend: {
        right: 0,
        top: 'middle',
        orient: 'vertical'
      },
      grid: {
        left: '3%',
        right: '15%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'value',
        axisLabel: { // x축 레이블 설정
          fontFamily: FONT_PROPS.PRIMARY, // x축 폰트 패밀리
          fontWeight: FONT_PROPS.MEDIUM, // x축 폰트 굵기
          fontSize: 14,
        },
      },
      yAxis: {
        type: 'category',
        data: months,
        axisLabel: { 
            fontFamily: FONT_PROPS.PRIMARY,
            fontWeight: FONT_PROPS.MEDIUM,
            fontSize: 14,
        },
      },
      series
    };
  
    chart.setOption(option);

    // 월별 bar 클릭 시
    // chart.on('click', function (params) {
    //   const clickedMonth = params.name;       // 예: '1월'
    //   const index = params.dataIndex;         // 0~11 (월 인덱스)
      
    //   console.log(`[${clickedMonth}] 클릭됨 (index: ${index})`);
    
    //   // 월 단위로 처리하는 로직 넣기
    //   setSelectedMonth(clickedMonth);
    // });

    // 월별 각 스택bar를 클릭 시
    // chart.on('click', function (params) {
    //   console.log('[스택 클릭됨]', {
    //     name: params.name, // 예: '1월'
    //     seriesName: params.seriesName, // 예: '보통'
    //     value: params.value // 예: 3
    //   });
  
    //   // 필요시 조건 분기도 가능
    //   switch(params.seriesName) {
    //     case '매우낮음': {
    //       setRenderRiskInfo('veryRow');
    //       break;
    //     }
    //     case '낮음': {
    //       setRenderRiskInfo('row');
    //       break;
    //     }
    //     case '보통': {
    //       setRenderRiskInfo('normal');
    //       break;
    //     }
    //     case '약간높음': {
    //       setRenderRiskInfo('littleHigh');
    //       break;
    //     }
    //     case '높음': {
    //       setRenderRiskInfo('High');
    //       break;
    //     }
    //     case '매우높음': {
    //       setRenderRiskInfo('veryHigh');
    //       break;
    //     }
    //     case '미평가': {
    //       setRenderRiskInfo('unevaluated');
    //       break;
    //     }
    //     default: { 
    //       console.log(`이상한 값이 들어옮 ${params.seriesName}`);
    //       break;
    //     }
    //   }
    // });

    return () => chart.dispose();
  }, [chartData]);

  return (
    <Card
      sx={{ 
        width: '100%',
        height: 'auto',
        //mx: 'auto',
        boxShadow: '0px 1px 4px rgba(0,0,0,0.07)',
        border: `1px solid ${theme.palette.divider}`, 
        borderRadius: "0.375rem", 
        bgcolor: 'var(--mui-palette-background-paper)',
        //overflow: 'hidden'
      }}
    >
      <CardContent>
        <div ref={chartRef} style={{ width: '100%', height: 500 }} />
      </CardContent>
    </Card>
  );
};

// 월별 상.중.하 위험성 추이
const MonthlyRiskEvaluationChart = ({ chartData }) => {
  const theme = useTheme();
  const chartRef = useRef(null);

  useEffect(() => {
    const chart = echarts.init(chartRef.current);

    // 월별 데이터
    const months = chartData?.months.map(item => { return `${item}월`}); 

    // 월별로 랜덤한 위험 단계 값
    const levelData = {
      하: chartData?.high_medium_low_risks.low, // 12개로 보여주기 (ex: 1월 ~ 12월)
      중: chartData?.high_medium_low_risks.medium,
      상: chartData?.high_medium_low_risks.high,
    };

    const levelColors = {
      하: '#B8FFB8',
      중: '#FFFF00',
      상: '#FF4444'
    };

    const series = Object.keys(levelData).map(level => ({
      name: level,
      type: 'bar',
      stack: 'total',
      label: {
        show: true,
        position: 'inside',
        formatter: function (params) {
          return params.value === 0 ? '' : params.value; // 0은 표시 안함
        },
        color: '#505050',
        fontWeight: FONT_PROPS.SEMIBOLD,
        fontSize: 12,
      },
      itemStyle: {
        color: levelColors[level]
      },
      data: levelData[level]
    }));

    const option = {
      title: {
        text: '월별 상.중.하 위험성 추이',
        left: 'left',
        textStyle: {
          fontFamily: FONT_PROPS.PRIMARY,
          fontWeight: FONT_PROPS.SEMIBOLD,
          fontSize: 16,
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' }
      },
      legend: {
        right: 0,
        top: 'middle',
        orient: 'vertical'
      },
      grid: {
        left: '3%',
        right: '15%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: months,
        axisLabel: { // x축 레이블 설정
          fontFamily: FONT_PROPS.PRIMARY, // x축 폰트 패밀리
          fontWeight: FONT_PROPS.MEDIUM, // x축 폰트 굵기
          fontSize: 14,
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          fontFamily: FONT_PROPS.PRIMARY,
          fontWeight: FONT_PROPS.MEDIUM,
          fontSize: 14,
        },
      },
      series
    };

    chart.setOption(option);
    return () => chart.dispose();
  }, [chartData]);

  return (
    <Card
      sx={{ 
        width: '100%',
        height: 'auto',
        //mx: 'auto',
        boxShadow: '0px 1px 4px rgba(0,0,0,0.07)',
        border: `1px solid ${theme.palette.divider}`, 
        borderRadius: "0.375rem", 
        bgcolor: 'var(--mui-palette-background-paper)',
        //overflow: 'hidden'
      }}
    >
      <CardContent>
        <div ref={chartRef} style={{ width: '100%', height: 500 }} />
      </CardContent>
    </Card>
  ); 
};
